import { useState, useEffect, useRef, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Link } from "@tiptap/extension-link";
import { Mention } from "@tiptap/extension-mention";
import { supabase } from "@/lib/supabase";
import { CHAT_MENTION_DEBOUNCE_MS } from "@/lib/dataRuntimeFlags";
import type { ProfileSimple } from "@/data/jobMessageTypes";
import { Button } from "@/components/ui/button";
import { Send, Smile, Loader2, Image, CheckCircle, X } from "lucide-react";
import { initials, avatarColor } from "@/lib/utils";
import { toast } from "sonner";
import { uploadAttachment, createSignedAttachmentUrl } from "@/services/attachment.service";
import { formatFileSize } from "@/data/attachmentTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface TiptapComposerProps {
  jobId: string;
  onSend: (content: { html: string; text: string; mentionUserIds: string[] }) => Promise<void>;
  loading?: boolean;
  isOrdersTab?: boolean;
}

interface MentionCommandProps {
  items: ProfileSimple[];
  command: (props: { id: string; label: string }) => void;
  range: { from: number; to: number };
}

export function TiptapComposer({ jobId, onSend, loading, isOrdersTab = false }: TiptapComposerProps) {
  const { profile } = useCurrentUser();
  const [hasContent, setHasContent] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestionItems, setSuggestionItems] = useState<ProfileSimple[]>([]);
  const [suggestionCommand, setSuggestionCommand] = useState<((props: { id: string; label: string }) => void) | null>(null);
  const [suggestionRange, setSuggestionRange] = useState<{ from: number; to: number } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<{
    file: File;
    localUrl: string;
    signedUrl?: string;
    attachmentId?: string;
    status: "uploading" | "done" | "error";
  } | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const editor = useEditor({
    editorProps: {
      attributes: {
        "data-placeholder": "Type a message... Use @ to mention someone",
      },
      handleKeyDown: (_view, event) => {
        if (event.key === "Enter" && !event.shiftKey && !suggestionOpen) {
          event.preventDefault();
          void handleSend();
          return true;
        }
        return false;
      },
    },
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        validate: (href) => /^https?:\/\//.test(href),
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention",
        },
        renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        renderHTML: ({ node }) => [
          "span",
          {
            "data-type": "mention",
            "data-id": node.attrs.id,
            "data-label": node.attrs.label ?? node.attrs.id,
            class: "mention bg-primary/10 rounded px-1 py-0.5 text-xs font-medium text-primary",
          },
          `@${node.attrs.label ?? node.attrs.id}`,
        ],
        suggestion: {
          char: "@",
          debounce: CHAT_MENTION_DEBOUNCE_MS,
          items: async ({ query }: { query: string }) => {
            return searchOrgProfiles(query);
          },
          render: () => {
            return {
              onStart: (props: MentionCommandProps) => {
                setSuggestionItems(props.items);
                setSuggestionCommand(() => props.command);
                setSuggestionRange(props.range);
                setSuggestionOpen(true);
              },
              onUpdate: (props: MentionCommandProps) => {
                setSuggestionItems(props.items);
                setSuggestionCommand(() => props.command);
                setSuggestionRange(props.range);
                setSuggestionOpen(true);
              },
              onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (event.key === "Escape") {
                  setSuggestionOpen(false);
                  return true;
                }
                if (event.key === "Enter" && suggestionItems.length > 0) {
                  event.preventDefault();
                  if (suggestionCommand) {
                    suggestionCommand({
                      id: suggestionItems[0].id,
                      label: suggestionItems[0].full_name || suggestionItems[0].email || "",
                    });
                  }
                  setSuggestionOpen(false);
                  return true;
                }
                return false;
              },
              onExit: () => {
                setSuggestionOpen(false);
                setSuggestionItems([]);
                setSuggestionCommand(null);
                setSuggestionRange(null);
              },
            };
          },
        },
      }),
    ],
    content: "",
    onUpdate: ({ editor: updatedEditor }) => {
      setHasContent(updatedEditor.getText().trim().length > 0);
    },
  });

  const handleSend = useCallback(async () => {
    if (!editor || suggestionOpen) return;
    const isEmpty = editor.isEmpty;
    if (isEmpty && !uploadPreview) return;

    const editorHtml = editor.getHTML();
    const text = editor.getText();
    const mentionUserIds: string[] = [];
    editor.state.doc.descendants((node) => {
      if (node.type.name === "mention" && node.attrs.id) {
        mentionUserIds.push(node.attrs.id);
      }
    });

    const uniqueIds = [...new Set(mentionUserIds)];

    let finalHtml = editorHtml;
    if (uploadPreview?.status === "done" && uploadPreview.signedUrl) {
      const safeName = escapeHtml(uploadPreview.file.name);
      const figureHtml = `<figure class="chat-invoice-figure" data-attachment-id="${uploadPreview.attachmentId}"><img src="${uploadPreview.signedUrl}" alt="${safeName}" class="chat-invoice-image" loading="lazy" /><button type="button" class="chat-invoice-delete" aria-label="Delete invoice image" title="Delete invoice image"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button><figcaption class="chat-invoice-caption"><span class="chat-invoice-filename">${safeName}</span> <span class="chat-invoice-size">(${formatFileSize(uploadPreview.file.size)})</span></figcaption></figure>`;

      if (finalHtml.trim()) {
        finalHtml = finalHtml + figureHtml;
      } else {
        finalHtml = figureHtml;
      }
    }

    try {
      await onSend({ html: finalHtml, text, mentionUserIds: uniqueIds });
      editor?.commands.clearContent();
      if (uploadPreview) {
        URL.revokeObjectURL(uploadPreview.localUrl);
        setUploadPreview(null);
      }
    } catch (err) {
      console.error("[TiptapComposer] onSend failed:", err);
      toast.error("Message failed to send");
    }
  }, [onSend, suggestionOpen, editor, uploadPreview]);

  const handleImageSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPEG, PNG, WebP, and GIF images are accepted.");
        e.target.value = "";
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File exceeds the 10 MB size limit.");
        e.target.value = "";
        return;
      }

      const localUrl = URL.createObjectURL(file);
      setUploadPreview({ file, localUrl, status: "uploading" });
      setIsUploading(true);
      const tempId = `temp-${Date.now()}`;

      try {
        const attachment = await uploadAttachment(
          file,
          "job",
          jobId,
          "invoice",
          profile?.full_name ?? "Unknown",
          `chat_invoice:pending:${tempId}`,
        );

        const signedUrl = await createSignedAttachmentUrl(attachment.filePath, "job-photos");

        setUploadPreview({
          file,
          localUrl,
          signedUrl,
          attachmentId: attachment.id,
          status: "done",
        });
      } catch {
        URL.revokeObjectURL(localUrl);
        setUploadPreview(null);
        toast.error("Failed to upload invoice image. Please try again.");
      } finally {
        setIsUploading(false);
        e.target.value = "";
      }
    },
    [jobId, profile?.full_name],
  );

  const canSend = hasContent || uploadPreview?.status === "done";


  useEffect(() => {
    if (!suggestionOpen) {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      return;
    }
    if (!suggestionItems.length || !suggestionRange) return;

    const editorEl = editor?.view.dom;
    if (!editorEl) return;

    const coords = editorEl.getBoundingClientRect();

    const container = document.createElement("div");
    container.className = "fixed z-[9999] bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto w-64";
    container.style.left = `${coords.left}px`;
    container.style.top = `${coords.bottom + window.scrollY}px`;

    suggestionItems.forEach((profile) => {
      const btn = document.createElement("button");
      btn.className = "w-full flex items-center gap-2 p-2 text-left hover:bg-accent rounded-lg transition-colors";
      const avatar = document.createElement("span");
      avatar.className = "inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-medium";
      avatar.style.backgroundColor = avatarColor(profile.id);
      avatar.textContent = initials(profile.full_name || profile.email || "U");

      const label = document.createElement("span");
      label.className = "font-medium text-sm truncate";
      label.textContent = profile.full_name || profile.email || "user";

      btn.append(avatar, label);
      if (profile.role) {
        const role = document.createElement("span");
        role.className = "text-xs text-muted-foreground capitalize";
        role.textContent = profile.role;
        btn.appendChild(role);
      }
      btn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (suggestionCommand) {
          suggestionCommand({ id: profile.id, label: profile.full_name || profile.email || "user" });
        }
        setSuggestionOpen(false);
      });
      container.addEventListener("mousedown", (e) => {
        e.stopPropagation();
      });
      container.appendChild(btn);
    });

    document.body.appendChild(container);
    popupRef.current = container;

    return () => {
      if (container.parentNode) container.remove();
      popupRef.current = null;
    };
  }, [suggestionOpen, suggestionItems, suggestionCommand, suggestionRange, editor]);

  if (!editor) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative">
      {uploadPreview && (
        <div className="mb-2 border border-input rounded-lg px-3 py-2 bg-background">
          <div className="flex items-center gap-3">
            <img
              src={uploadPreview.localUrl}
              alt={uploadPreview.file.name}
              className="h-12 w-12 object-cover rounded"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{uploadPreview.file.name}</div>
              <div className="text-xs text-muted-foreground">{formatFileSize(uploadPreview.file.size)}</div>
            </div>
            <div className="flex items-center gap-2">
              {uploadPreview.status === "uploading" && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Uploading...</span>
                </div>
              )}
              {uploadPreview.status === "done" && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle size={12} />
                  <span>Ready</span>
                </div>
              )}
              {uploadPreview.status === "error" && (
                <div className="flex items-center gap-1 text-xs text-destructive">
                  <span>Failed</span>
                </div>
              )}
              <button
                type="button"
                className="p-1 hover:text-destructive transition-colors"
                onClick={() => {
                  URL.revokeObjectURL(uploadPreview.localUrl);
                  setUploadPreview(null);
                }}
                aria-label="Remove image preview"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
      <EditorContent
        editor={editor}
        className="w-full min-h-[60px] max-h-[160px] overflow-y-auto border border-input rounded-lg px-3 py-2 text-sm text-muted-foreground bg-background focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/30"
      />
      <style>{`
        .ProseMirror {
          outline: none;
        }
        .ProseMirror:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
        }
        .mention {
          background-color: rgba(59, 130, 246, 0.1);
        }
        .chat-invoice-figure {
          position: relative;
          max-width: 400px;
          margin: 0;
          border: 1px solid rgba(var(--border));
          border-radius: 0.5rem;
          overflow: hidden;
          background: rgba(var(--muted));
        }
        .chat-invoice-image {
          width: 100%;
          aspect-ratio: 16 / 10;
          object-fit: cover;
          display: block;
          cursor: pointer;
        }
        .chat-invoice-delete {
          position: absolute;
          top: 4px;
          right: 4px;
          display: none;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.6);
          border: none;
          cursor: pointer;
          padding: 0;
          color: white;
        }
        .chat-invoice-figure:hover .chat-invoice-delete {
          display: flex;
        }
        .chat-invoice-caption {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 8px;
          font-size: 11px;
          color: var(--muted-foreground);
          gap: 8px;
        }
        .chat-invoice-filename {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }
        .chat-invoice-size {
          white-space: nowrap;
          opacity: 0.7;
        }
        .chat-invoice-status {
          font-size: 10px;
          color: var(--muted-foreground);
          opacity: 0.7;
        }
        .chat-invoice-figure[data-local="true"] .chat-invoice-delete {
          display: none;
        }
      `}</style>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            onClick={() => toast.warning("Emoji picker coming soon")}
          >
            <Smile size={14} />
          </button>
          {isOrdersTab && (
            <>
              <button
                type="button"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
                onClick={() => imageInputRef.current?.click()}
                disabled={isUploading}
                title="Attach invoice image"
              >
                <Image size={14} />
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                capture="environment"
                className="sr-only"
                onChange={handleImageSelect}
                disabled={isUploading}
              />
            </>
          )}
        </div>
        <Button
          size="sm"
          onClick={handleSend}
          disabled={loading || !canSend || isUploading}
          className="h-8"
        >
          {loading || isUploading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Send size={14} />
          )}
          <span className="ml-1">Send</span>
        </Button>
      </div>
    </div>
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

async function searchOrgProfiles(query: string): Promise<ProfileSimple[]> {
  if (!query.trim()) return [];
  const { data, error } = await supabase.rpc("list_org_profiles", {
    search_term: query,
  });

  if (error) {
    console.warn("Could not search profiles:", error);
    return [];
  }

  return (data ?? []) as ProfileSimple[];
}
